<script lang="ts">
    import { faCreditCard, faEdit, faMoneyBill, faUser, faWindowClose } from '@fortawesome/free-solid-svg-icons'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import type { BillEntity } from '../../modules/financialApi/bills/bill'
    import dayjs from 'dayjs'
    import _isToday from 'dayjs/plugin/isToday'
    dayjs.extend(_isToday)
    import { onMount } from 'svelte'
    import Popover from '../_common/popover.svelte'
    import PaymentsPurchaseShow from '../payments/payments-purchase-show.svelte'
    import PosForm from '../pos/pos-form.svelte'
    import Financial_4BillsForm from '../financial-4/bills/financial-4-bills-form.svelte'
    import Financial_4BillsFormPayment from '../financial-4/bills/financial-4-bills-form-payment.svelte'
    import Financial_4BillsRecurring from '../financial-4/bills/financial-4-bills-recurring.svelte'
    import { EntryTypeEnum } from '../../modules/financialApi/entries/entry.enum'
    import Zgrid from '../_common/z/zgrid.svelte'

    export let personId: string
    let show = false
    let entities: BillEntity[] = []

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            const dt = {
                creditDebit: 'credit',
                personId,
            }
            entities = await window.api.financials.bills.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Financeiro - A Receber da Pessoa'
        grid.subtitle = ''
        grid.fontSize = '13px'
        grid.showFilter = false
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Recebido em', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string, maxLength: 45 }))
        grid.cols.push(new GridCol({ content: 'A receber', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Recebido', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            let btnOpen = null
            if (item.source === 'zandupay') {
                btnOpen = new GridButton({
                    callback: () => openPurchaseShow(item.groupId),
                    icon: faCreditCard,
                    label: 'ZanduPay',
                })
            } else if (item.source === 'pos') {
                btnOpen = new GridButton({
                    callback: () => openPDV(item.invoiceId),
                    icon: faCreditCard,
                    label: 'PDV',
                })
            } else {
                btnOpen = new GridButton({
                    callback: () => goShowBill(item.billId),
                    icon: faEdit,
                    label: 'Editar',
                })
            }

            let btnPay = null
            if (item.source !== 'zandupay') {
                if (item.status === 95) {
                    btnPay = new GridButton({
                        callback: (): void => goShowPayment(item.billId),
                        icon: faMoneyBill,
                        label: 'Receber',
                        disabled: item.source === 'zandupay',
                    })
                } else {
                    btnPay = new GridButton({
                        callback: () => voidPayment(item.billId),
                        icon: faWindowClose,
                        label: 'Estornar',
                        disabled: item.source === 'zandupay',
                    })
                }
            }

            let description = item.description
            if (item.installmentTotal > 1) {
                description = '(' + item.installmentNumber + '/' + item.installmentTotal + ') ' + description
            }
            let line: GridLine = new GridLine()
            line.slots.push({ content: new GridBadge(evaluateBadge(item)) })
            if (item.source === 'zandupay') {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_purple, label: 'zandupay' }),
                })
            } else if (item.source === 'pos') {
                line.slots.push({ content: new GridBadge({ class: GridBadgeClassEnum.badge_purple, label: 'pdv' }) })
            } else if (item.source === 'recurring') {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'recorrente' }),
                })
            } else {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'digitação' }),
                })
            }
            line.slots.push({ content: item.dueAt })
            line.slots.push({ content: item.paidAt })
            line.slots.push({ content: description })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.status >= 100 ? item.paidValue : 0 })
            line.slots.push({ content: [btnPay, btnOpen] })
            grid.lines.push(line)
        }
    }

    function evaluateBadge(item: BillEntity): { label: string; class: string } {
        if (item.status === 100) {
            return {
                label: 'recebido',
                class: GridBadgeClassEnum.badge_success,
            }
        }
        const isToday = dayjs(item.dueAt).isToday()
        if (isToday) {
            return {
                label: 'vence hoje',
                class: GridBadgeClassEnum.badge_alert,
            }
        }
        if (dayjs(item.dueAt).diff(new Date(), 'day') >= 0) {
            return {
                label: 'a receber',
                class: GridBadgeClassEnum.badge_default,
            }
        }
        return {
            label: 'atrasado',
            class: GridBadgeClassEnum.badge_danger,
        }
    }

    let billId = null
    let showBill = false
    function goShowBill(id: string) {
        billId = id
        showBill = true
    }

    async function voidPayment(id: string) {
        if (!confirm('Deseja mesmo estornar o recebimento desta conta?')) return
        await window.api.financials.bills.void(id)
        await loadEntities()
    }

    let showPayment = false
    function goShowPayment(id: string) {
        billId = id
        showPayment = true
    }

    let purchaseId: string = null
    function openPurchaseShow(id: string) {
        purchaseId = id
    }

    async function closePurchaseShow() {
        purchaseId = null
        await loadEntities()
    }

    let invoiceId: string = null
    function openPDV(id: string) {
        invoiceId = id
    }

    async function closePDV() {
        show = true
        invoiceId = null

        setTimeout(async () => {
            await loadEntities()
            window.alert('Aviso: alterações feitas no PDV podem levar alguns instantes até refletirem no Contas a Receber.')
        }, 3000)
    }

    function goCloseBill(update: boolean) {
        showBill = false
        showPayment = false
        billId = null
        if (update) loadEntities()
    }
</script>

{#if purchaseId}
    <Popover title="Cobrança" width="1100px" height="80vh" on:close={closePurchaseShow}>
        <PaymentsPurchaseShow {purchaseId} on:close={closePurchaseShow} />
    </Popover>
{/if}

{#if invoiceId}
    <Popover title="Venda" width="1300px" height="90vh" on:close={() => (invoiceId = null)}>
        <PosForm {invoiceId} on:change={closePDV} on:cancel={() => (invoiceId = null)} />
    </Popover>
{/if}

{#if showBill}
    <Popover title="Novo recebível" width="970px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsForm {billId} creditDebit={EntryTypeEnum.credit} on:change={() => goCloseBill(true)}></Financial_4BillsForm>
    </Popover>
{/if}

{#if showPayment}
    <Popover title="Receber" width="840px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsFormPayment {billId} on:change={() => goCloseBill(true)}></Financial_4BillsFormPayment>
    </Popover>
{/if}

<Zgrid class="" {grid}></Zgrid>
